import { useState, useCallback } from 'react';
import { invokeApig } from '../libs/awsLib';

/**
 * Hook para gestionar el contenido dinámico de páginas guiadas.
 * Interactúa con los endpoints POST /content y POST /content/find.
 */
export const useContentManager = () => {
  const [isSaving,  setIsSaving]  = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState(null);

  // -------------------------------------------------------------------------
  // loadContent — POST /content/find
  // Carga el contenido almacenado de una página guiada.
  // Retorna { success, content: { sectionId: [] | {} } }
  // -------------------------------------------------------------------------
  const loadContent = useCallback(async (designId, pageId) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await invokeApig({
        path:   '/site/content/find',
        method: 'POST',
        body:   { designId, pageId },
      });

      if (response.Status === 0 || response.statusCode === 200) {
        return { success: true, content: response.content || {} };
      } else {
        throw new Error(response.message || 'Error al cargar el contenido');
      }
    } catch (err) {
      console.error('Error loading content:', err);
      setError(err.message);
      return { success: false, error: err.message, content: {} };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // -------------------------------------------------------------------------
  // saveContent — POST /content
  // Guarda el contenido completo de una página guiada.
  // content: { sectionId: [ {...campos} ] | {...campos} }
  // -------------------------------------------------------------------------
  const saveContent = useCallback(async (designId, pageId, content) => {
    try {
      setIsSaving(true);
      setError(null);

      const response = await invokeApig({
        path:   '/site/content',
        method: 'POST',
        body:   { designId, pageId, content },
      });

      if (response.Status === 0 || response.statusCode === 200) {
        return { success: true };
      } else {
        throw new Error(response.message || 'Error al guardar el contenido');
      }
    } catch (err) {
      console.error('Error saving content:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setIsSaving(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    loadContent,
    saveContent,
    isSaving,
    isLoading,
    error,
    clearError,
  };
};
